/**
 * Authentication Context
 * Manages user authentication state and role
 */

import { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { authAPI } from "utils/api";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  useEffect(() => {
    // Check if user is logged in on mount
    const storedToken = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);

        // REJECT CLIENT ACCESS - This dashboard is for administration only
        if (parsedUser.type === "client" || (!parsedUser.role && !parsedUser.fullName)) {
          console.warn("Client account detected - removing access to admin dashboard");
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          setUser(null);
          setToken(null);
          setLoading(false);
          return;
        }

        setUser(parsedUser);
        setToken(storedToken);
      } catch (error) {
        console.error("Error parsing stored user:", error);
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      // Validate input
      if (!email || !password) {
        return { success: false, error: "Email and password are required" };
      }

      const response = await authAPI.login(email, password);

      // Backend returns { token, user: { id, fullName, email, role } } for users
      // Or { token, client: { id, email, phone, gender } } for clients
      if (response.token) {
        let userData;

        if (response.user) {
          // Normalize role so property owners always get property owner dashboard (not e.g. check-in/check-out)
          let role = response.user.role;
          if (role === "Property Owner" || role === "property_owner") {
            role = "Property_Owner";
          }
          // Regular user (Admin, Operations, etc.) or Property Owner
          userData = {
            id: response.user.id,
            fullName: response.user.fullName,
            email: response.user.email,
            role,
            type: response.user.type || "user",
          };
        } else if (response.client) {
          // REJECT CLIENT ACCESS - This dashboard is for administration only
          // This should not happen as backend rejects clients, but handle it just in case
          throw new Error(
            "Client accounts cannot access the administration dashboard. This dashboard is for administrators, operations staff, and property owners only."
          );
        } else {
          throw new Error("Invalid response format from server");
        }

        setUser(userData);
        setToken(response.token);
        localStorage.setItem("token", response.token);
        localStorage.setItem("user", JSON.stringify(userData));
        return { success: true, user: userData };
      }

      throw new Error(response.message || "Login failed. Invalid response from server.");
    } catch (error) {
      console.error("Login error:", error);
      console.error("Error details:", {
        message: error.message,
        status: error.status,
        data: error.data,
      });

      // Handle specific error messages from backend
      // The backend returns the actual error message in error.message
      // We should use it directly, especially for 403 status (pending, suspended, etc.)
      let errorMessage = "Login failed. Please check your credentials.";

      // Extract error message from different possible locations
      if (error.message) {
        errorMessage = error.message;
      } else if (error.data && error.data.message) {
        errorMessage = error.data.message;
      } else if (error.data && error.data.error) {
        errorMessage = error.data.error;
      }

      // Handle common backend error messages by status code
      if (error.status === 401) {
        // For 401, use the backend message or default
        errorMessage = errorMessage || "Invalid email or password";
      } else if (error.status === 403) {
        // For 403, always use the backend message (account pending, suspended, etc.)
        // This is critical - we want to show the exact message from backend
        errorMessage = errorMessage || "Account access denied. Please contact support.";
      } else if (error.status === 400) {
        errorMessage = errorMessage || "Invalid request";
      }

      console.log("Final error message to display:", errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  };

  const value = {
    user,
    token,
    login,
    logout,
    loading,
    isAuthenticated: !!token && !!user,
    userRole: user?.role || null, // Backend returns role as string (role name)
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
